// components/modals/SkillsContent.jsx
//
// Settings-modal tab for project-scoped agent Skills. Styled to match the
// more polished sibling tabs (MeetingsContent.jsx's zinc card list, Modal.js
// for the create/edit form, an in-app confirm dialog instead of the native
// window.confirm for delete) rather than the older slate/inline-form
// convention this file started from.
//
// GET /data/skills/list returns system skills (is_system: true, read-only)
// UNION this project's own skills. Only project-owned rows get Edit/Toggle/
// Delete controls.
//
// Skills use the open Agent Skills format (agentskills.io) -- the same one as
// github.com/anthropics/skills and github.com/openai/skills. "Import" uploads
// a skill as-is (a .zip/.skill archive, a whole folder, or a single SKILL.md)
// and the backend does all parsing and validation. Bundled scripts only ever
// run inside an isolated sandbox (no network, no access to the server), the
// same way Claude and OpenAI run skill scripts.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiZap,
  FiPlus,
  FiTrash2,
  FiLoader,
  FiEdit,
  FiEye,
  FiLock,
  FiChevronDown,
  FiAlertTriangle,
  FiUpload,
  FiFileText,
  FiDownload,
  FiFolder,
  FiChevronRight,
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { DataState } from '../../context/DataProvider';
import { useAccess } from '../../services/access';
import Modal from '../base/Modal';
import * as skillsApi from '../../services/skillsApi';
import SkillEditor from './SkillEditor';
import SkillFromDocument, { DocxPreview } from './SkillFromDocument';
import { errorMessage, formatBytes } from './skillsShared';

// Starter SKILL.md in the Agent Skills format (agentskills.io). `name` is
// the skill's id: lowercase letters, numbers and hyphens. `allowed-tools` is
// left blank rather than pre-filled with a guessed name, since the valid
// names depend on this project's connected integrations and would go stale.
const SKILL_TEMPLATE = `---
name: my-skill-name
description: What this skill does, and when the agent should use it.
allowed-tools:
---

# My Skill Name

Explain, step by step, how the agent should behave when this skill applies.

- Be specific about when this skill should apply.
- List any formatting rules the response should follow.
- If you filled in \`allowed-tools\` above, explain when to use each one.
- Longer reference material can go in references/*.md next to this file;
  point to it from here (e.g. "see references/style-guide.md").
`;

// Folder uploads can drag in VCS/OS noise; don't send it at all.
const IGNORED_UPLOAD_PARTS = ['/.git/', '/node_modules/', '/__MACOSX/'];
const MAX_UPLOAD_FILES = 500;

function uploadableFiles(fileList) {
  return Array.from(fileList || []).filter((file) => {
    const path = `/${file.webkitRelativePath || file.name}`;
    return (
      !IGNORED_UPLOAD_PARTS.some((part) => path.includes(part)) &&
      !path.endsWith('/.DS_Store')
    );
  });
}

const FILE_GROUPS = [
  { kind: 'reference', label: 'References' },
  { kind: 'script', label: 'Scripts' },
  { kind: 'asset', label: 'Assets' },
  { kind: 'other', label: 'Other files' },
];

function downloadSkillTemplate() {
  const blob = new Blob([SKILL_TEMPLATE], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'SKILL.md';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Import dropdown: a skill archive or single SKILL.md, a whole skill folder,
// or a template download for whoever's about to write one from scratch.
// Files go to POST /data/skills/import as-is -- no client-side parsing.
function ImportFileMenu({
  onFilesSelected,
  onDownloadFolderTemplate,
  onFromDocument,
  disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleChange = (e) => {
    const files = uploadableFiles(e.target.files);
    setIsOpen(false);
    if (files.length) onFilesSelected(files);
    // Reset so choosing the same file again still fires onChange.
    e.target.value = '';
  };

  const itemClass =
    'flex w-full items-start gap-2 px-3 py-2 text-left text-xs text-zinc-200 hover:bg-zinc-800';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-[11px] font-medium text-zinc-300 transition-colors hover:border-zinc-600 hover:text-white disabled:cursor-wait disabled:opacity-60"
      >
        {disabled ? (
          <FiLoader className="h-3 w-3 animate-spin" />
        ) : (
          <FiUpload className="h-3 w-3" />
        )}
        Import
        <FiChevronDown
          className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="absolute right-0 z-10 mt-1 w-64 overflow-hidden rounded-md border border-zinc-700 bg-zinc-900 shadow-lg">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={itemClass}
          >
            <FiFileText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />
            <span>
              Upload .zip, .skill or SKILL.md
              <span className="block text-[10px] text-zinc-500">
                Skills from Anthropic or OpenAI work as-is
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => folderInputRef.current?.click()}
            className={`${itemClass} border-t border-zinc-800`}
          >
            <FiFolder className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />
            <span>
              Upload skill folder
              <span className="block text-[10px] text-zinc-500">
                SKILL.md with references/, scripts/, assets/
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              onFromDocument();
            }}
            className={`${itemClass} border-t border-zinc-800`}
          >
            <FiFileText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />
            <span>
              From a Word document
              <span className="block text-[10px] text-zinc-500">
                Documents of a kind get this .docx's look
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              downloadSkillTemplate();
            }}
            className={`${itemClass} border-t border-zinc-800`}
          >
            <FiDownload className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />
            <span>
              Download SKILL.md template
              <span className="block text-[10px] text-zinc-500">
                Single file, instructions only
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              onDownloadFolderTemplate();
            }}
            className={`${itemClass} border-t border-zinc-800`}
          >
            <FiDownload className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />
            <span>
              Download skill folder template (.zip)
              <span className="block text-[10px] text-zinc-500">
                SKILL.md + references/, scripts/, assets/
              </span>
            </span>
          </button>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,.skill,.md,.markdown,application/zip,text/markdown"
        className="hidden"
        onChange={handleChange}
      />
      <input
        ref={folderInputRef}
        type="file"
        webkitdirectory=""
        directory=""
        multiple
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}

function FilePreviewModal({ preview, onClose }) {
  return (
    <Modal title={preview?.path || 'File'} isOpen={!!preview} onClose={onClose}>
      <div className="p-4">
        {preview?.loading ? (
          <div className="flex h-24 items-center justify-center">
            <FiLoader className="animate-spin text-[#5652CF]" />
          </div>
        ) : preview?.docx ? (
          <DocxPreview blob={preview.docx} />
        ) : preview?.is_text ? (
          <>
            {/* Rendered as plain text only -- uploaded files never run as HTML. */}
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-md border border-zinc-800 bg-zinc-950 p-3 font-mono text-[11px] leading-relaxed text-zinc-300">
              {preview.content}
            </pre>
            {preview.truncated && (
              <p className="mt-2 text-[10px] text-zinc-500">
                Showing the first 200 KB of {formatBytes(preview.size)}.
              </p>
            )}
          </>
        ) : (
          <p className="text-xs text-zinc-400">
            Binary file ({formatBytes(preview?.size || 0)}) &mdash; no preview.
          </p>
        )}
      </div>
    </Modal>
  );
}

// Compact on/off switch. Uses a real (visually hidden) checkbox input so the
// state is keyboard/screen-reader accessible, and an in-flow (not
// absolutely-positioned) thumb -- the same translate-on-flex-child technique
// used by the app's other toggles (e.g. workflow3/ToggleButton.js) -- rather
// than an absolutely-positioned span with no explicit `left`, which only
// happens to land correctly via the browser's static-position fallback.
function SkillToggle({ checked, disabled, onChange, label }) {
  return (
    <label
      className={`relative inline-flex flex-shrink-0 items-center ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={label}
      />
      <span className="sr-only">{label}</span>
      <span
        className={`flex h-6 w-11 items-center rounded-full border transition-colors duration-200 ${
          checked
            ? 'border-[#5652CF] bg-[#5652CF]'
            : 'border-zinc-600 bg-zinc-700'
        } peer-focus-visible:ring-2 peer-focus-visible:ring-[#5652CF]/50`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </span>
    </label>
  );
}

function ConfirmDeleteModal({ skill, isDeleting, onCancel, onConfirm }) {
  return (
    <Modal title="Delete Skill" isOpen={!!skill} onClose={onCancel}>
      <div className="space-y-4 p-4">
        <div className="flex items-start gap-3 rounded-md border border-red-900/40 bg-red-950/20 p-3">
          <FiAlertTriangle className="mt-0.5 flex-shrink-0 text-red-400" />
          <p className="text-sm text-zinc-300">
            Delete{' '}
            <span className="font-semibold text-zinc-100">{skill?.name}</span>?
            This permanently removes the skill and cannot be undone.
          </p>
        </div>
        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="rounded-md px-4 py-2 text-xs text-zinc-300 transition-colors hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex h-8 w-24 items-center justify-center rounded-md bg-red-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:bg-red-900/60"
          >
            {isDeleting ? <FiLoader className="animate-spin" /> : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const SkillsContent = () => {
  const { userGoogleId: accessToken } = DataState();
  // Adding, changing and deleting skills needs template:manage; without it
  // every skill shows read-only (view files, export), like system skills.
  const canManage = useAccess().can('template:manage');

  const [skills, setSkills] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // { mode: 'create' | 'edit' | 'view', skill } while the editor is open
  const [editor, setEditor] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // skill pending delete confirmation
  const [isImporting, setIsImporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null); // skill.id showing details
  const [preview, setPreview] = useState(null); // file preview modal state
  const [fromDocumentOpen, setFromDocumentOpen] = useState(false);

  const projectSkills = useMemo(
    () => skills.filter((s) => !s.is_system),
    [skills],
  );
  const systemSkills = useMemo(
    () => skills.filter((s) => s.is_system),
    [skills],
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [skillsRes, toolsRes] = await Promise.all([
          skillsApi.listSkills(accessToken),
          skillsApi.listAvailableSkillTools(accessToken),
        ]);
        setSkills(skillsRes.data?.skills || []);
        setAvailableTools(toolsRes.data?.tools || []);
      } catch (err) {
        toast.error(errorMessage(err, 'Failed to load skills.'));
      } finally {
        setIsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditorSaved = (saved, { created }) => {
    setSkills((prev) =>
      created
        ? [saved, ...prev]
        : prev.map((s) => (s.id === saved.id ? saved : s)),
    );
    if (saved.import_warnings?.length) setExpandedId(saved.id);
    setEditor(null);
  };

  const handleExport = async (skill) => {
    try {
      await skillsApi.exportSkill(accessToken, skill.id, skill.slug);
    } catch (err) {
      toast.error(errorMessage(err, 'Export failed.'));
    }
  };

  const handleImport = async (files) => {
    if (files.length > MAX_UPLOAD_FILES) {
      toast.error(`That folder has more than ${MAX_UPLOAD_FILES} files.`);
      return;
    }
    setIsImporting(true);
    try {
      const { data: skill } = await skillsApi.importSkill(accessToken, files);
      setSkills((prev) => [skill, ...prev]);
      const warnings = skill.import_warnings?.length || 0;
      if (warnings) {
        setExpandedId(skill.id);
        toast.success(
          `Imported "${skill.name}" with ${warnings} note${warnings > 1 ? 's' : ''} -- see details.`,
        );
      } else {
        toast.success(`Imported "${skill.name}".`);
      }
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to import skill.'));
    } finally {
      setIsImporting(false);
    }
  };

  const openPreview = async (skill, file) => {
    setPreview({ path: file.path, loading: true });
    try {
      if (file.path.toLowerCase().endsWith('.docx')) {
        const docx = await skillsApi.getSkillDocx(
          accessToken,
          skill.id,
          file.path,
        );
        setPreview({ path: file.path, docx, loading: false });
        return;
      }
      const { data } = await skillsApi.getSkillFileContent(
        accessToken,
        skill.id,
        file.path,
      );
      setPreview({ ...data, loading: false });
    } catch (err) {
      setPreview(null);
      toast.error(errorMessage(err, 'Could not load that file.'));
    }
  };

  const handleToggleStatus = async (skill) => {
    setProcessingId(skill.id);
    const previous = skill.is_active;
    setSkills((prev) =>
      prev.map((s) => (s.id === skill.id ? { ...s, is_active: !previous } : s)),
    );
    try {
      await skillsApi.toggleSkillStatus(accessToken, skill.id);
    } catch (err) {
      setSkills((prev) =>
        prev.map((s) =>
          s.id === skill.id ? { ...s, is_active: previous } : s,
        ),
      );
      toast.error(errorMessage(err, 'Failed to update status.'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async () => {
    const skill = deleteTarget;
    if (!skill) return;
    setProcessingId(skill.id);
    try {
      await skillsApi.deleteSkill(accessToken, skill.id);
      setSkills((prev) => prev.filter((s) => s.id !== skill.id));
      toast.success('Skill deleted.');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to delete skill.'));
    } finally {
      setProcessingId(null);
    }
  };

  const renderSkillDetails = (skill) => {
    const files = (skill.files || []).filter(
      (f) => f.path !== 'SKILL.md' && f.kind !== 'agent_config',
    );
    const hasScripts = files.some((f) => f.kind === 'script');
    const deps = skill.dependencies || [];
    const warnings = skill.import_warnings || [];
    const meta = [
      skill.license && ['License', skill.license],
      skill.compatibility && ['Compatibility', skill.compatibility],
      ...Object.entries(skill.metadata || {}).map(([k, v]) => [k, v]),
    ].filter(Boolean);

    return (
      <div className="mt-3 space-y-3 border-t border-zinc-800 pt-3 text-[10px]">
        {meta.length > 0 && (
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
            {meta.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-zinc-500">{k}</dt>
                <dd className="break-words text-zinc-300">{v}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}

        {deps.length > 0 && (
          <p className="text-zinc-400">
            Requires:{' '}
            {deps
              .map((d) => d.description || `${d.type} ${d.value}`)
              .join(', ')}
          </p>
        )}

        {warnings.length > 0 && (
          <ul className="space-y-1 rounded-md border border-amber-900/40 bg-amber-950/20 p-2 text-amber-300/90">
            {warnings.map((w) => (
              <li key={w} className="flex items-start gap-1.5">
                <FiAlertTriangle className="mt-0.5 h-2.5 w-2.5 flex-shrink-0" />
                {w}
              </li>
            ))}
          </ul>
        )}

        {hasScripts && (
          <p className="flex items-start gap-1.5 text-zinc-500">
            <FiLock className="mt-0.5 h-2.5 w-2.5 flex-shrink-0" />
            Scripts run only in an isolated sandbox with no internet and no
            access to your other data. Results are saved as new files.
          </p>
        )}

        {FILE_GROUPS.map(({ kind, label }) => {
          const group = files.filter((f) =>
            kind === 'other'
              ? !['reference', 'script', 'asset'].includes(f.kind)
              : f.kind === kind,
          );
          if (!group.length) return null;
          return (
            <div key={kind}>
              <p className="mb-1 font-semibold uppercase tracking-wider text-zinc-500">
                {label} ({group.length})
              </p>
              <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                {group.map((f) => (
                  <li key={f.path}>
                    <button
                      type="button"
                      onClick={() => openPreview(skill, f)}
                      className="flex w-full items-center justify-between gap-2 rounded px-1.5 py-0.5 text-left text-zinc-300 hover:bg-zinc-800"
                    >
                      <span className="truncate font-mono">{f.path}</span>
                      <span className="flex-shrink-0 text-zinc-600">
                        {formatBytes(f.size)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSkillCard = (skill, { editable }) => {
    const isProcessingThis = processingId === skill.id;
    const bundled = (skill.files || []).filter(
      (f) => f.path !== 'SKILL.md' && f.kind !== 'agent_config',
    );
    const isExpanded = expandedId === skill.id;
    const hasDetails =
      bundled.length > 0 ||
      skill.license ||
      skill.compatibility ||
      skill.import_warnings?.length > 0 ||
      skill.dependencies?.length > 0;
    return (
      <div
        key={skill.id}
        className="group rounded-md border border-zinc-800 bg-zinc-800/40 p-3 transition-all duration-200 hover:border-zinc-700 hover:bg-zinc-800/60"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-xs font-bold text-zinc-200">
                {skill.name}
              </h3>
              {skill.is_system && (
                <span className="flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800 px-1.5 py-0.5 text-[9px] font-medium text-zinc-400">
                  <FiLock size={9} />{' '}
                  {skill.source === 'builtin' ? 'Built-in' : 'System'}
                </span>
              )}
              {skill.import_warnings?.length > 0 && (
                <FiAlertTriangle
                  className="h-3 w-3 flex-shrink-0 text-amber-400"
                  title="Imported with notes -- see details"
                />
              )}
            </div>
            {skill.description && (
              <p className="mt-0.5 line-clamp-1 text-[10px] text-zinc-500">
                {skill.description}
              </p>
            )}
            {skill.tool_names?.length > 0 && (
              <p className="mt-1 line-clamp-1 text-[10px] text-zinc-600">
                Tools: {skill.tool_names.join(', ')}
              </p>
            )}
            {hasDetails && (
              <button
                type="button"
                onClick={() => setExpandedId(isExpanded ? null : skill.id)}
                className="mt-1 flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-300"
                aria-expanded={isExpanded}
              >
                <FiChevronRight
                  className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
                {bundled.length > 0
                  ? `${bundled.length} bundled file${bundled.length > 1 ? 's' : ''}`
                  : 'Details'}
              </button>
            )}
          </div>

          <div className="flex flex-shrink-0 items-center gap-3">
            {editable ? (
              <>
                <span
                  className={`text-[10px] font-medium ${
                    isProcessingThis
                      ? 'text-zinc-500'
                      : skill.is_active
                        ? 'text-emerald-400'
                        : 'text-zinc-500'
                  }`}
                >
                  {isProcessingThis
                    ? '...'
                    : skill.is_active
                      ? 'Active'
                      : 'Inactive'}
                </span>
                <SkillToggle
                  checked={skill.is_active}
                  disabled={isProcessingThis}
                  onChange={() => handleToggleStatus(skill)}
                  label={`Toggle ${skill.name} ${skill.is_active ? 'off' : 'on'}`}
                />
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    onClick={() => setEditor({ mode: 'edit', skill })}
                    title="Edit"
                    className="rounded p-1 text-zinc-400 transition-colors hover:bg-[#5652CF]/10 hover:text-[#5652CF]"
                  >
                    <FiEdit className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleExport(skill)}
                    title="Export .zip"
                    className="rounded p-1 text-zinc-400 transition-colors hover:bg-[#5652CF]/10 hover:text-[#5652CF]"
                  >
                    <FiDownload className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(skill)}
                    disabled={isProcessingThis}
                    title="Delete"
                    className="rounded p-1 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:cursor-wait"
                  >
                    {isProcessingThis ? (
                      <FiLoader className="h-3 w-3 animate-spin" />
                    ) : (
                      <FiTrash2 className="h-3 w-3" />
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    onClick={() => setEditor({ mode: 'view', skill })}
                    title="View files"
                    className="rounded p-1 text-zinc-400 transition-colors hover:bg-[#5652CF]/10 hover:text-[#5652CF]"
                  >
                    <FiEye className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleExport(skill)}
                    title="Export .zip"
                    className="rounded p-1 text-zinc-400 transition-colors hover:bg-[#5652CF]/10 hover:text-[#5652CF]"
                  >
                    <FiDownload className="h-3 w-3" />
                  </button>
                </div>
                <span
                  className={`rounded border px-1.5 py-0.5 text-[9px] font-medium ${
                    skill.is_active
                      ? 'border-emerald-900/50 bg-emerald-950/30 text-emerald-400'
                      : 'border-zinc-700 bg-zinc-800 text-zinc-500'
                  }`}
                >
                  {skill.is_active ? 'Active' : 'Inactive'}
                </span>
              </>
            )}
          </div>
        </div>
        {isExpanded && renderSkillDetails(skill)}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <FiLoader className="animate-spin text-xl text-[#5652CF]" />
      </div>
    );
  }

  return (
    <div className="p-1">
      <SkillEditor
        isOpen={!!editor}
        mode={editor?.mode}
        skill={editor?.skill}
        availableTools={availableTools}
        accessToken={accessToken}
        onClose={() => setEditor(null)}
        onSaved={handleEditorSaved}
      />

      <FilePreviewModal preview={preview} onClose={() => setPreview(null)} />
      <SkillFromDocument
        isOpen={fromDocumentOpen}
        onClose={() => setFromDocumentOpen(false)}
        onCreated={(skill) => {
          setSkills((prev) => [skill, ...prev]);
          setFromDocumentOpen(false);
        }}
        accessToken={accessToken}
      />

      <ConfirmDeleteModal
        skill={deleteTarget}
        isDeleting={processingId === deleteTarget?.id}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-zinc-100">
            <FiZap className="h-4 w-4 text-[#5652CF]" />
            Skills
          </h3>
          <p className="text-[10px] text-zinc-500">
            Reusable instructions and tools your agents apply automatically.
          </p>
        </div>
        <div className={canManage ? 'flex items-center gap-2' : 'hidden'}>
          <ImportFileMenu
            onFilesSelected={handleImport}
            onFromDocument={() => setFromDocumentOpen(true)}
            onDownloadFolderTemplate={() =>
              skillsApi
                .downloadSkillTemplateZip(accessToken)
                .catch((err) =>
                  toast.error(errorMessage(err, 'Template download failed.')),
                )
            }
            disabled={isImporting}
          />
          <button
            onClick={() => setEditor({ mode: 'create', skill: null })}
            className="flex items-center gap-1.5 rounded bg-[#5652CF] px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#4642bf]"
          >
            <FiPlus className="h-3 w-3" /> Add Skill
          </button>
        </div>
      </div>

      <div className="mb-6 space-y-3">
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
          Your Skills
        </h4>
        {projectSkills.length > 0 ? (
          projectSkills.map((skill) =>
            renderSkillCard(skill, { editable: canManage }),
          )
        ) : (
          <p className="rounded-md border border-dashed border-zinc-800 px-3 py-4 text-center text-[11px] text-zinc-600">
            You haven&apos;t created any custom skills yet.
          </p>
        )}
      </div>

      {systemSkills.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
            System Skills
          </h4>
          {systemSkills.map((skill) =>
            renderSkillCard(skill, { editable: false }),
          )}
        </div>
      )}
    </div>
  );
};

export default SkillsContent;
